import React, { useMemo } from "react";

const defaultYLabel = (n) => {
  if (Math.abs(n) >= 1_000_000_000_000) return `$${(n / 1_000_000_000_000).toFixed(1)}T`;
  if (Math.abs(n) >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const AreaChart = ({ data, height = 220, yLabel = defaultYLabel }) => {
  const id = useMemo(() => `c${Math.random().toString(36).slice(2, 8)}`, []);
  if (!data || data.length < 2) {
    return (
      <div style={{ height, display: "grid", placeItems: "center", color: "var(--tx-3)", fontSize: "var(--t-sm)" }}>
        No data yet
      </div>
    );
  }
  const w = 760;
  const h = height;
  const padL = 48;
  const padR = 8;
  const padT = 10;
  const padB = 24;
  const max = Math.max(...data.map((d) => d.value)) * 1.08 || 1;
  const min = 0;
  const range = max - min || 1;
  const xStep = (w - padL - padR) / (data.length - 1);
  const pts = data.map((d, i) => {
    const x = padL + i * xStep;
    const y = padT + (1 - (d.value - min) / range) * (h - padT - padB);
    return [x, y, d];
  });
  const path = pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const area = `${path} L${pts[pts.length - 1][0]},${h - padB} L${padL},${h - padB} Z`;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => ({
    y: padT + (1 - t) * (h - padT - padB),
    label: yLabel(min + t * range),
  }));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height, display: "block" }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.16" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={padL} y1={t.y} x2={w - padR} y2={t.y} stroke="var(--bd)" strokeWidth="1" strokeDasharray={i === 0 ? "0" : "2 4"} />
          <text x={padL - 6} y={t.y + 3} fontSize="10" textAnchor="end" fill="var(--tx-3)" fontFamily="var(--f-mono)">
            {t.label}
          </text>
        </g>
      ))}
      {data.map((d, i) =>
        i % 2 === 0 ? (
          <text
            key={i}
            x={padL + i * xStep}
            y={h - 6}
            fontSize="10"
            textAnchor="middle"
            fill="var(--tx-3)"
            fontFamily="var(--f-mono)"
            letterSpacing="0.04em"
          >
            {d.label.toUpperCase()}
          </text>
        ) : null
      )}
      <path d={area} fill={`url(#${id})`} />
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export default AreaChart;
